import { makeAutoObservable, runInAction, toJS } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { carbonInventoryService } from "../services";
import { generateTargetYearOptions } from "../utils/year-helpers";

const toPositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return parsed;
};
const translate = (t, key, fallback, values) => {
  if (typeof t === "function") return t(key, values);
  return fallback;
};
class CarbonTargetsStore {
  carbonTargets = new Map();
  loading = false;
  saving = false;
  carbonTargetsV2 = {};
  hasDataChanges = false;
  selectedYear = new Date().getFullYear();
  items = [];
  deleteTarget = null;

  page = 1;
  pageSize = 10;
  total = 0;
  totalPages = 0;
  filters = { targetYear: "All" };
  loadingList = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: false });
  }

  buildListParams = () => {
    const params = {
      page: this.page,
      limit: this.pageSize,
    };

    if (this.filters.targetYear !== "All") {
      params.targetYear = this.filters.targetYear;
    }
    return params;
  };

  setPage = (page) => {
    this.page = toPositiveInt(page, 1);
  };

  setPageSize = (size) => {
    this.pageSize = toPositiveInt(size, 10);
    this.page = 1;
  };
  get yearFilterOptions() {
    const values = generateTargetYearOptions(2019, 10).map((year) => ({
      value: String(year),
      label: String(year),
    }));
    return [{ value: "All", label: "All" }, ...values];
  }

  setFilter = (key, value) => {
    this.filters[key] = value;
    this.page = 1;
  };

  setSelectedYear = (year) => {
    const next = Number(year);
    if (!Number.isFinite(next)) return;
    this.selectedYear = next;
    runInAction(() => {
      this.hasDataChanges = true;
      this.carbonTargetsV2.year = next;
    });
  };

  getCarbonYearKey = (year) => {
    return `carbon_${year}`;
  };

  getCarbonTargets = (year) => {
    const key = this.getCarbonYearKey(year);
    if (!this.carbonTargets.has(key)) {
      this.carbonTargets.set(key, {
        _id: "",
        scope1: {},
        scope2: {},
      });
    }
    return this.carbonTargets.get(key);
  };

  /**
   * Set a specific carbon value
   * @param {string} scopeId - 'scope1' or 'scope2'
   * @param {string} fieldKey - Field name
   * @param {any} value - Value to set
   * @param {number} year - Target year
   */
  setCarbonValue = (scopeId, fieldKey, value, year) => {
    this.hasDataChanges = true;
    const key = this.getCarbonYearKey(year);
    // const current = this.getCarbonTargets(year);
    const current = this.carbonTargetsV2;
    const updated = {
      ...current,
      [scopeId]: {
        ...(current[scopeId] || {}),
        [fieldKey]: value ? value : 0,
      },
    };
    this.carbonTargetsV2 = updated;
    // this.carbonTargets.set(key, updated);
  };

  saveCarbonTargets = async (year) => {
    runInAction(() => {
      this.saving = true;
    });

    try {
      // const carbonTargets = this.getCarbonTargets(year);
      const payload = {
        year,
        ...this.carbonTargetsV2,
      };

      const data = await carbonInventoryService.saveCarbonTargets(payload);

      const targets = data?.target || {};
      runInAction(() => {
        this.carbonTargetsV2 = targets;
        this.hasDataChanges = false;
      });

      return { success: true, data: this.carbonTargetsV2 };
    } catch (error) {
      return {
        success: false,
        error: error?.message || "Failed to save carbon targets.",
      };
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  getCarbonTargetsList = async () => {
    runInAction(() => {
      this.loadingList = true;
    });

    try {
      const data = await carbonInventoryService.getCarbonTargetsList(
        this.buildListParams(),
        {
          throwOnError: false,
        },
      );

      if (!data) {
        throw new Error("Failed to load carbon settings list.");
      }
      const payload = data?.targets;
      const items = payload?.data || [];
      const pagination = payload.pagination || {};

      runInAction(() => {
        this.items = items;
        this.total = Number(pagination.total) || 0;
        this.totalPages = Number(pagination.totalPages) || 0;
      });
    } catch (error) {
      showToast.error(error?.message || "Failed to load carbon settings list.");
      runInAction(() => {
        this.items = [];
        this.total = 0;
        this.totalPages = 0;
      });
    } finally {
      runInAction(() => {
        this.loadingList = false;
      });
    }
  };

  updataeCarbonTargets = async (id, year) => {
    runInAction(() => {
      this.saving = true;
    });

    try {
      // const carbonTargets = this.getCarbonTargets(year);
      const payload = {
        year,
        ...this.carbonTargetsV2,
      };

      const data = await carbonInventoryService.updateCarbonTargets(
        id,
        payload,
      );

      const targets = data?.target || {};
      runInAction(() => {
        this.hasDataChanges = false;
      });

      return { success: true, data: this.carbonTargetsV2 };
    } catch (error) {
      return {
        success: false,
        error: error?.message || "Failed to save carbon targets.",
      };
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  setDeleteTarget = (target) => {
    this.deleteTarget = target;
  };

  closeDeleteDialog = () => {
    this.deleteTarget = null;
  };

  deleteCarbonTarget = async (t) => {
    if (!this.deleteTarget?.id && !this.deleteTarget?._id) return;
    try {
      const data = await carbonInventoryService.deleteCarbonTargetById(
        this.deleteTarget?._id,
      );
      if (data?.result?.status) {
        showToast.success(
          translate(t, "Carbon Target deleted.", "Carbon Target deleted."),
        );
        await this.getCarbonTargetsList();
        this.deleteTarget = null;
      }
    } catch (error) {
      showToast.error(
        error?.message ||
          translate(t, "Failed to delete target.", "Failed to delete target."),
      );
    }
  };

  clearCarbonTargets = (year) => {
    const key = this.getCarbonYearKey(year);
    this.carbonTargets.delete(key);
    this.carbonTargetsV2 = {};
    this.selectedYear = new Date().getFullYear();
    this.hasDataChanges = false;
  };

  getCarbonTargetDetails = async (id) => {
    try {
      const data = await carbonInventoryService.getCarbonTargetDetailsById(id);
      runInAction(() => {
        this.carbonTargetsV2 = data.result;
        this.selectedYear = data.result.year;
      });
    } catch (error) {
      showToast.error(error?.message || "Failed to load!.");
    }
  };
}

export const carbonTargetsStore = new CarbonTargetsStore();
